import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import { GoldBtn } from '../components/UI';
import { getUserTasteProfile } from '../lib/supabase';

function StatBar({ label, pct, color = 'var(--gold)' }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ color: 'var(--text)', fontSize: 14 }}>{label}</span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

export default function TasteProfilePage({ onAuthOpen, onAdminClick, onChatClick, onBattleClick, onRankingsClick, onTasteClick, onFilmClick }) {
  const { user, profile } = useAuth();
  const [taste,   setTaste]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getUserTasteProfile(user.id).then(data => {
      setTaste(data);
      setLoading(false);
    });
  }, [user]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <Navbar onAuthOpen={onAuthOpen} onAdminClick={onAdminClick} onChatClick={onChatClick} onBattleClick={onBattleClick} onRankingsClick={onRankingsClick} onTasteClick={onTasteClick} />

      <div style={{ flex: 1, maxWidth: 860, width: '100%', margin: '0 auto', padding: '32px 16px' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px,5vw,38px)', fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>
            🎭 Your Taste Profile
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>
            Rate films to discover your cinema personality.
          </p>
        </div>

        {/* Not logged in */}
        {!user && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎭</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 22, marginBottom: 10 }}>Log in to see your profile</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 24 }}>Rate films to unlock your personalized taste profile and recommendations.</p>
            <GoldBtn onClick={onAuthOpen}>Log In</GoldBtn>
          </div>
        )}

        {/* Loading */}
        {user && loading && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          </div>
        )}

        {/* Need more ratings */}
        {user && !loading && !taste && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🌱</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 22, marginBottom: 10 }}>
              Rate more films to unlock your profile
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 6 }}>
              You need to rate at least <strong style={{ color: 'var(--gold)' }}>5 films</strong> (score 6 or above) to generate your taste profile.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              Go explore films, rate the ones you love, and come back here!
            </p>
          </div>
        )}

        {/* Full profile */}
        {user && !loading && taste && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Profile card */}
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 16, padding: '24px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--gold-dim), var(--gold))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 32, fontWeight: 700, color: 'var(--black)',
                fontFamily: "'Playfair Display', serif",
              }}>
                {profile?.username?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: 'var(--text)', fontWeight: 700 }}>
                  @{profile?.username}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
                  Rated <span style={{ color: 'var(--gold)' }}>{taste.totalRated} films</span> · Profile based on ratings 6/10 and above
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>

              {/* Language breakdown */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 17, marginBottom: 18 }}>
                  🌍 Language Taste
                </h3>
                {taste.topLangs.map(({ lang, pct }) => (
                  <StatBar key={lang} label={lang} pct={pct} />
                ))}
              </div>

              {/* Genre breakdown */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 17, marginBottom: 18 }}>
                  🎬 Favourite Genres
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {taste.topGenres.map((g, i) => (
                    <div key={g} style={{
                      padding: '6px 14px', borderRadius: 20,
                      background: i === 0 ? 'rgba(212,175,55,0.15)' : 'var(--surface2)',
                      border: `1px solid ${i === 0 ? 'var(--gold-dim)' : 'var(--border2)'}`,
                      color: i === 0 ? 'var(--gold)' : 'var(--text-dim)',
                      fontSize: 14, fontWeight: i === 0 ? 600 : 400,
                    }}>
                      {i === 0 ? '⭐ ' : ''}{g}
                    </div>
                  ))}
                </div>

                {/* Movie vs Series */}
                <div style={{ marginTop: 20 }}>
                  <h4 style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 12, letterSpacing: 0.3 }}>MOVIES VS SERIES</h4>
                  {taste.moviePct > 0  && <StatBar label="🎥 Movies"  pct={taste.moviePct}  color="var(--gold)" />}
                  {taste.seriesPct > 0 && <StatBar label="📺 Series"  pct={taste.seriesPct} color="#60a5fa" />}
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {taste.recommended.length > 0 && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 17, marginBottom: 6 }}>
                  ✨ Watch Next
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 18 }}>
                  Based on your taste — films you haven't rated yet
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                  {taste.recommended.map(film => {
                    const hasPoster = film.poster_url && film.poster_url.startsWith('http');
                    return (
                      <div key={film.id} onClick={() => onFilmClick && onFilmClick(film)}
                        style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.18s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-dim)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        <div style={{ height: 100, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                          {hasPoster
                            ? <img src={film.poster_url} alt={film.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 36 }}>{film.poster_emoji || '🎬'}</span>
                          }
                        </div>
                        <div style={{ padding: '8px 10px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{film.title}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{film.language}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
